import { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Dialog } from "primereact/dialog";
import { useAxiosRQ, apiPost } from "../../api/apiREST";
import { useUserData } from "../../contexts/UserDataContext";
import Button from "../../components/core/Button";

interface Category {
  id: string;
  name: string;
  name_en: string;
  description: string;
  created_at: string;
  updated_at: string;
}

interface Product {
  id: string;
  name: string;
  description: string;
  image: string;
  image_url: string;
  url: string | null;
  url2: string | null;
  url3: string | null;
  is_active: boolean;
  winning_product: boolean;
  featured_product: boolean;
  category: Category;
  order: number;
  min_price: number;
  max_price: number;
  profit_amount: number;
  created_at: string;
  updated_at: string;
  score: number | null;
}

interface ProductsResponse {
  count: number;
  next_page: string | null;
  previous_page: string | null;
  current_page: number;
  total_pages: number;
  results: Product[];
}

interface TokensResponse {
  boostcoin_balance: number;
  powerpass_balance: number;
}

const MIN_REQUIRED_BOOSTCOINS = 5;

const isForbiddenTrendProductsError = (error: unknown) =>
  axios.isAxiosError(error) && error.response?.status === 403;

const clampScoreValue = (value: number) => Math.max(0, Math.min(5, value));

interface ScoreAppearance {
  stroke: string;
  text: string;
  background: string;
}

const getScoreAppearance = (score: number | null): ScoreAppearance => {
  if (typeof score !== "number") {
    return {
      stroke: "#9ca3af",
      text: "#6b7280",
      background: "rgba(156, 163, 175, 0.15)",
    };
  }

  const value = clampScoreValue(score);

  if (value >= 4.5) {
    return {
      stroke: "#166534",
      text: "#166534",
      background: "rgba(22, 101, 52, 0.15)",
    };
  }

  if (value >= 3.5) {
    return {
      stroke: "#22c55e",
      text: "#15803d",
      background: "rgba(34, 197, 94, 0.15)",
    };
  }

  if (value >= 2) {
    return {
      stroke: "#facc15",
      text: "#92400e",
      background: "rgba(250, 204, 21, 0.2)",
    };
  }

  if (value >= 1) {
    return {
      stroke: "#ef4444",
      text: "#b91c1c",
      background: "rgba(239, 68, 68, 0.18)",
    };
  }

  return {
    stroke: "#9ca3af",
    text: "#6b7280",
    background: "rgba(156, 163, 175, 0.15)",
  };
};

const ScoreIndicator = ({
  score,
  size = 48,
}: {
  score: number | null;
  size?: number;
}) => {
  const hasScore = typeof score === "number" && !Number.isNaN(score);
  const clampedScore = hasScore ? clampScoreValue(score as number) : null;
  const appearance = getScoreAppearance(clampedScore);
  const progress =
    clampedScore !== null ? Math.round((clampedScore / 5) * 100) : 0;
  const displayValue = clampedScore !== null ? clampedScore.toFixed(1) : "--";

  const fontSize = Math.max(11, Math.floor(size * 0.32));

  return (
    <div
      className="relative flex items-center justify-center rounded-full shadow-sm"
      style={{
        width: size,
        height: size,
        backgroundColor: appearance.background,
      }}
    >
      <svg
        viewBox="0 0 36 36"
        style={{ width: size, height: size }}
        className="absolute inset-0"
      >
        <path
          stroke="#e5e7eb"
          strokeWidth="3"
          fill="none"
          pathLength={100}
          d="M18 2a16 16 0 1 1 0 32 16 16 0 0 1 0-32"
        />
        <path
          stroke={appearance.stroke}
          strokeWidth="3"
          fill="none"
          strokeLinecap="round"
          pathLength={100}
          strokeDasharray={100}
          strokeDashoffset={clampedScore !== null ? 100 - progress : 100}
          d="M18 2a16 16 0 1 1 0 32 16 16 0 0 1 0-32"
        />
      </svg>
      <span
        className="font-semibold leading-none"
        style={{ color: appearance.text, fontSize }}
      >
        {displayValue}
      </span>
    </div>
  );
};

const ProductCard = ({
  product,
  onSelect,
}: {
  product: Product;
  onSelect: (product: Product) => void;
}) => {
  return (
    <div className="relative bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
      <div className="aspect-square w-full overflow-hidden bg-gray-100">
        <img
          src={product.image_url || product.image}
          alt={product.name}
          className="w-full h-full object-cover"
          onError={(e) => {
            (e.target as HTMLImageElement).src = "/images/hero.png";
          }}
        />
      </div>
      <div className="p-4 flex flex-col gap-4">
        <div className="flex items-start justify-between gap-3">
          <h3 className="flex-1 text-BodyText/16/Medium text-gray-950 line-clamp-2">
            {product.name}
          </h3>
          <ScoreIndicator score={product.score} />
        </div>
        <p className="text-BodyText/14/Regular text-gray-600 line-clamp-3 leading-6 h-16">
          {product.description}
        </p>
        <button
          type="button"
          onClick={() => onSelect(product)}
          className="mt-2 w-full px-4 py-2 rounded-lg bg-purple-500 hover:bg-purple-600 text-white text-sm font-medium transition-all duration-200"
        >
          افزودن به فروشگاه
        </button>
      </div>
    </div>
  );
};

export default function Products() {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [isProcessingPurchase, setIsProcessingPurchase] = useState(false);
  const [purchaseErrorMessage, setPurchaseErrorMessage] = useState<
    string | null
  >(null);
  const [hasManuallyDismissedModal, setHasManuallyDismissedModal] =
    useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isProductModalVisible, setIsProductModalVisible] = useState(false);
  const [copiedLinkLabel, setCopiedLinkLabel] = useState<string | null>(null);
  const copyTimeoutRef = useRef<number | null>(null);
  const itemsPerPage = 10;

  const {
    data: categoriesData,
    isLoading: isLoadingCategories,
    error: categoriesError,
  } = useAxiosRQ("/categories/", "categories", {}, { retry: false });

  const categories = useMemo(() => {
    const data = categoriesData as Category[] | undefined;
    return (data ?? [])
      .filter((category) => !!category?.name_en && !!category?.name)
      .map((category) => ({ name: category.name, value: category.name_en }));
  }, [categoriesData]);

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  // Build query params with category filter
  const buildParams = (
    additionalParams: Record<string, any> = {},
    includeCategory = true,
    includePagination = false
  ) => {
    const params: Record<string, any> = { ...additionalParams };
    if (includeCategory && selectedCategory) {
      params.category = selectedCategory;
    }
    if (includePagination) {
      params.page = currentPage;
      params.page_size = itemsPerPage;
    }
    return params;
  };

  // Fetch featured products
  const {
    data: featuredData,
    isLoading: isLoadingFeatured,
    error: featuredError,
    refetch: refetchFeatured,
  } = useAxiosRQ(
    "/trend-products/",
    "featured-products",
    buildParams({ featured_product: true }, false),
    { retry: false }
  );

  // Fetch winning products
  const {
    data: winningData,
    isLoading: isLoadingWinning,
    error: winningError,
    refetch: refetchWinning,
  } = useAxiosRQ(
    "/trend-products/",
    "winning-products",
    buildParams({ winning_product: true }, false),
    { retry: false }
  );

  // Fetch all products
  const {
    data: allProductsData,
    isLoading: isLoadingAll,
    error: allProductsError,
    refetch: refetchAllProducts,
  } = useAxiosRQ(
    "/trend-products/",
    "all-products",
    buildParams({}, true, true),
    {
      retry: false,
    }
  );

  const {
    tokens: tokensData,
    tokensLoading: tokensLoadingState,
    tokensError,
    refetchTokens,
  } = useUserData();

  const isTrendProductsForbidden = useMemo(
    () =>
      isForbiddenTrendProductsError(featuredError) ||
      isForbiddenTrendProductsError(winningError) ||
      isForbiddenTrendProductsError(allProductsError),
    [featuredError, winningError, allProductsError]
  );

  useEffect(() => {
    if (isTrendProductsForbidden && !hasManuallyDismissedModal) {
      setShowSubscriptionModal(true);
      setPurchaseErrorMessage(null);
    }

    if (!isTrendProductsForbidden) {
      setShowSubscriptionModal(false);
      setHasManuallyDismissedModal(false);
      setPurchaseErrorMessage(null);
    }
  }, [isTrendProductsForbidden, hasManuallyDismissedModal]);

  // Get first 2 featured products
  const featuredProducts = useMemo(() => {
    const data = featuredData as ProductsResponse | undefined;
    return data?.results?.slice(0, 2) || [];
  }, [featuredData]);

  const winningProducts =
    (winningData as ProductsResponse | undefined)?.results || [];
  const allProductsResponse = allProductsData as ProductsResponse | undefined;
  const allProducts = allProductsResponse?.results || [];
  const totalProductsCount = allProductsResponse?.count ?? 0;
  const totalPagesFromResponse = allProductsResponse?.total_pages;
  const totalPages = Math.max(
    totalPagesFromResponse && totalPagesFromResponse > 0
      ? totalPagesFromResponse
      : Math.ceil(totalProductsCount / itemsPerPage),
    1
  );
  const hasNextPage = !!allProductsResponse?.next_page;
  const hasPreviousPage = !!allProductsResponse?.previous_page;
  const displayStartIndex =
    totalProductsCount === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const displayEndIndex =
    totalProductsCount === 0
      ? 0
      : Math.min(currentPage * itemsPerPage, totalProductsCount);
  const noAccessMessage = "محصولات قابل دسترسی نیستند";

  const productLinkItems = useMemo(
    () =>
      selectedProduct
        ? [
            { label: "تامین کننده 1", value: selectedProduct.url },
            { label: "تامین کننده 2", value: selectedProduct.url2 },
            { label: "تامین کننده 3", value: selectedProduct.url3 },
          ]
            .filter((link) => !!link.value)
            .map((link) => ({
              label: link.label,
              value: link.value as string,
            }))
        : [],
    [selectedProduct]
  );

  const boostcoinBalance = (tokensData as TokensResponse | undefined)
    ?.boostcoin_balance;
  const hasRequiredTokens =
    typeof boostcoinBalance === "number" &&
    boostcoinBalance >= MIN_REQUIRED_BOOSTCOINS;
  const isTokensLoadingState = tokensLoadingState;

  const handlePurchaseSubscription = async () => {
    if (isProcessingPurchase) {
      return;
    }

    setIsProcessingPurchase(true);
    setPurchaseErrorMessage(null);

    try {
      const response = await apiPost(
        "/subscriptions/purchase/?subscription_type=trend_products"
      );

      if (response.error) {
        const errorMessage =
          typeof response.error === "string"
            ? response.error
            : response.error?.detail ||
              "خطا در ثبت درخواست. لطفاً دوباره تلاش کنید.";
        setPurchaseErrorMessage(errorMessage);
        return;
      }

      await Promise.allSettled([
        refetchFeatured(),
        refetchWinning(),
        refetchAllProducts(),
        refetchTokens(),
      ]);
    } catch (error) {
      setPurchaseErrorMessage("خطا در ثبت درخواست. لطفاً دوباره تلاش کنید.");
    } finally {
      setIsProcessingPurchase(false);
    }
  };

  const handleCloseSubscriptionModal = () => {
    setShowSubscriptionModal(false);
    setHasManuallyDismissedModal(true);
    setPurchaseErrorMessage(null);
  };

  const handleRedirectToTickets = () => {
    const ticketTitle = "درخواست خرید بوست‌کوین برای Trend Products";
    const descriptionLines = [
      "کاربر قصد فعال‌سازی اشتراک Trend Products را دارد اما موجودی کافی ندارد.",
      `موجودی فعلی بوست‌کوین: ${boostcoinBalance ?? 0}`,
      "لطفاً جهت خرید بوست‌کوین و تکمیل فرآیند فعال‌سازی راهنمایی بفرمایید.",
    ];
    const searchParams = new URLSearchParams();
    searchParams.set("ticketTitle", ticketTitle);
    searchParams.set("ticketDescription", descriptionLines.join("\n"));

    handleCloseSubscriptionModal();
    navigate(`/support?${searchParams.toString()}`);
  };

  const handleHideSubscriptionModal = () => {
    if (!isTrendProductsForbidden) {
      setShowSubscriptionModal(false);
      setHasManuallyDismissedModal(false);
      setPurchaseErrorMessage(null);
    }
  };

  const handleOpenProductModal = (product: Product) => {
    setSelectedProduct(product);
    setIsProductModalVisible(true);
    setCopiedLinkLabel(null);
  };

  const handleCloseProductModal = () => {
    setIsProductModalVisible(false);
    setSelectedProduct(null);
    setCopiedLinkLabel(null);
    if (copyTimeoutRef.current) {
      clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = null;
    }
  };

  const handleCopyProductLink = async (
    value: string | null | undefined,
    label: string
  ) => {
    if (!value) {
      return;
    }
    try {
      await navigator.clipboard.writeText(value);
      setCopiedLinkLabel(label);
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
      copyTimeoutRef.current = window.setTimeout(() => {
        setCopiedLinkLabel(null);
        copyTimeoutRef.current = null;
      }, 2000);
    } catch (error) {
      console.error("Failed to copy product link:", error);
    }
  };

  // Reset to page 1 when category changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory]);

  return (
    <div className="flex flex-col justify-center items-center w-full gap-6 pb-8">
      {/* <img src="/images/hero.png" alt="products" className="w-full" /> */}

      {/* Featured and Winning Products */}
      <div className="flex justify-center items-start w-full gap-6 px-4 flex-wrap lg:flex-nowrap">
        {/* Winning Products Box */}
        <div className="flex flex-col w-full lg:w-1/2 gap-6 p-6 bg-white rounded-[15px] shadow-md border border-gray-100">
          <div className="flex justify-start items-end gap-2">
            <img src="/icons/star.svg" alt="star" className="w-5 h-5" />
            <p className="text-BodyText/18/Bold text-gray-950">
              محصولات منتخب هفته
            </p>
          </div>
          {isLoadingWinning ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-950"></div>
            </div>
          ) : winningProducts.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {winningProducts.slice(0, 2).map((product: Product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  onSelect={handleOpenProductModal}
                />
              ))}
            </div>
          ) : (
            <div className="flex justify-center items-center py-12 text-gray-500">
              {isTrendProductsForbidden ? noAccessMessage : "محصولی یافت نشد"}
            </div>
          )}
        </div>

        {/* Featured Products Box */}
        <div className="flex flex-col w-full lg:w-1/2 gap-6 p-6 bg-white rounded-[15px] shadow-md border border-gray-100">
          <div className="flex justify-start items-end gap-2">
            <img src="/icons/fire.svg" alt="fire" className="w-5 h-5" />
            <p className="text-BodyText/18/Bold text-gray-950">
              محصولات پرفروش هفته
            </p>
          </div>
          {isLoadingFeatured ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-950"></div>
            </div>
          ) : featuredProducts.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {featuredProducts.slice(0, 2).map((product: Product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  onSelect={handleOpenProductModal}
                />
              ))}
            </div>
          ) : (
            <div className="flex justify-center items-center py-12 text-gray-500">
              {isTrendProductsForbidden ? noAccessMessage : "محصولی یافت نشد"}
            </div>
          )}
        </div>
      </div>

      {/* Categories Filter */}
      <div className="flex justify-start items-center w-full gap-3 flex-wrap">
        <p className="text-BodyText/16/Medium text-gray-950">دسته بندی‌ها:</p>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setSelectedCategory("")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
              selectedCategory === ""
                ? "bg-gray-950 text-white shadow-md"
                : "bg-white text-gray-700 hover:bg-gray-50"
            }`}
          >
            همه
          </button>
          {isLoadingCategories ? (
            <div className="px-4 py-2 text-sm text-gray-500">
              در حال دریافت دسته‌بندی‌ها...
            </div>
          ) : categoriesError ? (
            <div className="px-4 py-2 text-sm text-red-600">
              خطا در دریافت دسته‌بندی‌ها
            </div>
          ) : (
            categories.map((category) => (
              <button
                key={category.value}
                onClick={() => setSelectedCategory(category.value)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  selectedCategory === category.value
                    ? "bg-gray-950 text-white shadow-md"
                    : "bg-white text-gray-700 hover:bg-gray-50"
                }`}
              >
                {category.name}
              </button>
            ))
          )}
        </div>
      </div>

      {/* All Products */}
      <div className="w-full px-4">
        <div className="mb-4">
          <h2 className="text-Title/24/Bold text-gray-950">همه محصولات</h2>
        </div>
        {isLoadingAll ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-950"></div>
          </div>
        ) : allProducts.length > 0 ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {allProducts.map((product: Product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  onSelect={handleOpenProductModal}
                />
              ))}
            </div>

            {/* Pagination - Only show when there are multiple pages */}
            {totalProductsCount > itemsPerPage && (
              <div className="flex flex-col items-center justify-center gap-4 mt-8">
                <div className="flex items-center gap-2">
                  {/* Previous Button */}
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(prev - 1, 1))
                    }
                    disabled={currentPage === 1 || !hasPreviousPage}
                    className={`px-4 py-2 rounded-lg border transition-all duration-200 ${
                      currentPage === 1 || !hasPreviousPage
                        ? "bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed"
                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    قبلی
                  </button>

                  {/* Page Numbers */}
                  <div className="flex items-center gap-2">
                    {Array.from({ length: totalPages }).map((_, index) => {
                      const page = index + 1;
                      // Show first page, last page, current page, and pages around current
                      if (
                        page === 1 ||
                        page === totalPages ||
                        (page >= currentPage - 1 && page <= currentPage + 1)
                      ) {
                        return (
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            className={`px-4 py-2 rounded-lg border transition-all duration-200 ${
                              currentPage === page
                                ? "bg-purple-500 text-white border-purple-500"
                                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                            }`}
                          >
                            {page}
                          </button>
                        );
                      } else if (
                        page === currentPage - 2 ||
                        page === currentPage + 2
                      ) {
                        return (
                          <span key={page} className="text-gray-400 px-2">
                            ...
                          </span>
                        );
                      }
                      return null;
                    })}
                  </div>

                  {/* Next Button */}
                  <button
                    onClick={() =>
                      setCurrentPage((prev) =>
                        hasNextPage ? Math.min(prev + 1, totalPages) : prev
                      )
                    }
                    disabled={currentPage === totalPages || !hasNextPage}
                    className={`px-4 py-2 rounded-lg border transition-all duration-200 ${
                      currentPage === totalPages || !hasNextPage
                        ? "bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed"
                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    بعدی
                  </button>
                </div>

                {/* Page Info */}
                <div className="text-sm text-gray-600">
                  نمایش {displayStartIndex} تا {displayEndIndex} از{" "}
                  {totalProductsCount} محصول
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="flex justify-center items-center py-12 text-gray-500 bg-white rounded-xl">
            {isTrendProductsForbidden ? noAccessMessage : "محصولی یافت نشد"}
          </div>
        )}
      </div>

      <Dialog
        visible={isProductModalVisible && !!selectedProduct}
        onHide={handleCloseProductModal}
        header={
          <div className="flex justify-between items-center w-full">
            <span className="text-Title/20/Bold text-gray-900">
              {selectedProduct?.name ?? "جزئیات محصول"}
            </span>
            <button
              type="button"
              onClick={handleCloseProductModal}
              className="text-BodyText/14/Regular text-gray-500 hover:text-gray-700 transition-colors"
            >
              بستن
            </button>
          </div>
        }
        modal
        closable={false}
        dismissableMask={true}
        blockScroll
        className="product-details-dialog p-fluid"
        contentClassName="product-details-dialog-content"
        maskClassName="product-details-dialog-mask"
        style={{ width: "90vw", maxWidth: "640px", direction: "rtl" }}
        breakpoints={{ "960px": "60vw", "641px": "90vw" }}
      >
        {selectedProduct && (
          <div className="flex flex-col gap-5 text-right">
            <div className="flex flex-col gap-4 sm:flex-row">
              <div className="w-full sm:w-48 sm:h-48 rounded-lg overflow-hidden bg-gray-100">
                <img
                  src={selectedProduct.image_url || selectedProduct.image}
                  alt={selectedProduct.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = "/images/hero.png";
                  }}
                />
              </div>
              <div className="flex-1 flex flex-col gap-3">
                <p className="text-BodyText/14/Regular text-gray-700 leading-7">
                  {selectedProduct.description}
                </p>
                <div className="flex items-center gap-2 text-BodyText/14/Regular text-gray-600">
                  <span>دسته‌بندی:</span>
                  <span>{selectedProduct.category?.name ?? "-"}</span>
                </div>
                <div className="flex items-center gap-3 text-BodyText/14/Regular text-gray-600">
                  <span>امتیاز:</span>
                  <ScoreIndicator score={selectedProduct.score} size={56} />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="rounded-lg border border-gray-100 p-3 flex flex-col gap-1">
                <span className="text-BodyText/12/Regular text-gray-500">
                  قیمت
                </span>
                <span className="text-BodyText/14/Bold text-gray-900">
                  {`$${selectedProduct.min_price.toLocaleString("en-US")}`}
                </span>
              </div>
              <div className="rounded-lg border border-gray-100 p-3 flex flex-col gap-1">
                <span className="text-BodyText/12/Regular text-gray-500">
                  قیمت پیشنهادی
                </span>
                <span className="text-BodyText/14/Bold text-gray-900">
                  {`$${selectedProduct.max_price.toLocaleString("en-US")}`}
                </span>
              </div>
              <div className="rounded-lg border border-gray-100 p-3 flex flex-col gap-1">
                <span className="text-BodyText/12/Regular text-gray-500">
                  سود
                </span>
                <span className="text-BodyText/14/Bold text-gray-900">
                  {`$${selectedProduct.profit_amount.toLocaleString("en-US")}`}
                </span>
              </div>
            </div>

            {productLinkItems.length > 0 && (
              <div className="flex flex-col gap-3">
                <span className="text-BodyText/14/Bold text-gray-900">
                  لینک‌ها
                </span>
                <div className="flex flex-wrap gap-2">
                  {productLinkItems.map((link) => (
                    <button
                      key={link.label}
                      type="button"
                      onClick={() =>
                        handleCopyProductLink(link.value, link.label)
                      }
                      className={`px-4 py-2 rounded-lg text-BodyText/14/Medium transition-colors border ${
                        copiedLinkLabel === link.label
                          ? "bg-green-100 border-green-200 text-green-700"
                          : "bg-gray-100 hover:bg-gray-200 border-transparent text-gray-700"
                      }`}
                    >
                      {copiedLinkLabel === link.label ? "کپی شد" : link.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Dialog>

      <Dialog
        visible={showSubscriptionModal}
        onHide={handleHideSubscriptionModal}
        header={
          <div className="flex justify-between items-center w-full">
            <span className="text-Title/20/Bold text-gray-900">
              فعال‌سازی اشتراک محصولات ترند
            </span>
            <button
              type="button"
              onClick={handleCloseSubscriptionModal}
              className="text-BodyText/14/Regular text-gray-500 hover:text-gray-700 transition-colors"
            >
              بستن
            </button>
          </div>
        }
        modal
        closable={false}
        dismissableMask={false}
        blockScroll
        className="trend-products-dialog p-fluid"
        contentClassName="trend-products-dialog-content"
        maskClassName="trend-products-dialog-mask"
        style={{ width: "90vw", maxWidth: "520px", direction: "rtl" }}
        breakpoints={{ "960px": "60vw", "641px": "90vw" }}
      >
        <div className="flex flex-col gap-4 text-right">
          <p className="text-BodyText/16/Regular text-gray-700 leading-7">
            دسترسی به فهرست محصولات ترند فقط برای کاربران دارای اشتراک فعال
            امکان‌پذیر است.
          </p>
          {isTokensLoadingState ? (
            <div className="flex justify-center items-center py-6">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600"></div>
            </div>
          ) : tokensError ? (
            <>
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-BodyText/14/Regular text-red-600">
                در دریافت موجودی بوست‌کوین خطایی رخ داد. لطفاً دوباره تلاش کنید.
              </div>
              <Button
                size="m"
                variant="outlined"
                color="gray"
                label="تلاش دوباره"
                type="button"
                onClick={() => refetchTokens()}
              />
            </>
          ) : hasRequiredTokens ? (
            <>
              <div className="rounded-lg border border-purple-200 bg-purple-50 px-4 py-3">
                <p className="text-BodyText/14/Bold text-purple-700">
                  موجودی بوست‌کوین شما: {boostcoinBalance}
                </p>
                <p className="mt-1 text-BodyText/12/Regular text-purple-600">
                  برای فعال‌سازی اشتراک «Trend Products» باید از 5 بوست‌کوین خود
                  استفاده کنید. با فشردن دکمه زیر، درخواست فعال‌سازی ارسال
                  می‌شود.
                </p>
              </div>
              {purchaseErrorMessage && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-BodyText/12/Regular text-red-600">
                  {purchaseErrorMessage}
                </div>
              )}
              <Button
                size="m"
                color="purple"
                label="ارسال درخواست فعال‌سازی"
                type="button"
                isLoading={isProcessingPurchase}
                disabled={isProcessingPurchase}
                onClick={handlePurchaseSubscription}
              />
            </>
          ) : (
            <>
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                <p className="text-BodyText/14/Bold text-amber-700">
                  موجودی بوست‌کوین شما: {boostcoinBalance ?? 0}
                </p>
                <p className="mt-1 text-BodyText/12/Regular text-amber-600">
                  برای فعال‌سازی اشتراک «Trend Products» حداقل به 5 بوست‌کوین
                  نیاز دارید. لطفاً برای خرید توکن از طریق ثبت تیکت اقدام کنید.
                </p>
              </div>
              <Button
                size="m"
                color="purple"
                label="ثبت تیکت برای خرید توکن"
                type="button"
                onClick={handleRedirectToTickets}
              />
            </>
          )}
        </div>
      </Dialog>
    </div>
  );
}
